/**
 * Blueprints — the site's theme workbench (AB#650).
 *
 * Three columns of blueprints, one per scope (Account | Site | User); one tile is
 * selected, and the bottom half edits it (Fonts & Colors / Standard Files / Custom
 * Files) next to a live preview. Which one the SITE builds with is a separate
 * question — the starred tile answers it: `settings.account_theme_id`, else the
 * bundled theme.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  useTheme,
  useThemes,
  useUpdateSiteSettings,
  themeKeys,
  accountThemeKeys,
  useSiteSettings,
  useAccountThemes,
  useCreateAccountTheme,
  useDuplicateAccountTheme,
  useRenameAccountTheme,
  useDeleteAccountTheme,
  type AccountThemeScope,
} from '@wordpuppi/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { confirmDialog } from '@/lib/dialog';
import { useEditorContext } from '@/lib/editorContext';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { BlueprintColumns } from '@/components/theme/BlueprintColumns';
import { BlueprintTabs } from '@/components/theme/BlueprintTabs';
import { PreviewPane } from '@/components/theme/PreviewPane';
import type { DuplicateTarget } from '@/components/theme/BlueprintColumns';
import {
  accountBlueprint,
  blueprintKey,
  stockBlueprint,
  type Blueprint,
  type BlueprintColumn,
} from '@/components/theme/blueprint';

export const Route = createFileRoute('/sites/$siteSlug/theme')({
  component: ThemePage,
  // Input type stays all-optional so existing <Link to="/sites/$siteSlug/theme">
  // callers don't have to pass a search object.
  validateSearch: (search: { bp?: unknown }): { bp?: string } => ({
    /** Selected blueprint: `site`, `user:<id>` or `account:<id>`. */
    bp: typeof search.bp === 'string' ? search.bp : undefined,
  }),
});

const SCOPE_TITLE: Record<AccountThemeScope, string> = {
  account: 'Account',
  site: 'Site',
  user: 'User',
};

const SCOPE_OPTION: Record<AccountThemeScope, string> = {
  account: 'Account — every seat',
  site: 'Site — this site only',
  user: 'User — just you',
};

/** AB#644: stock themes group by what they are for; anything else sorts last. */
const STOCK_ORDER: Record<string, number> = {
  general: 0,
  portfolio: 1,
  consultant: 2,
  blog: 3,
};

/** AB#644: the six stock tiles are a lot of column — let people put them away. */
const STOCK_HIDDEN_KEY = 'wpp-blueprints-stock-hidden';

function storedStockHidden(): boolean {
  try {
    return localStorage.getItem(STOCK_HIDDEN_KEY) === '1';
  } catch {
    return false; // private mode / storage disabled
  }
}

const errorText = (err: unknown) =>
  err instanceof Error ? err.message : 'Something went wrong';

function ThemePage() {
  const { siteSlug } = Route.useParams();
  const { bp: bpParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useTheme(siteSlug);
  const { data: accountThemesData, isLoading: blueprintsLoading } = useAccountThemes(siteSlug);
  // `true`: the stock tiles paint their swatch and list their files from this.
  const { data: bundledData } = useThemes(true);
  const { data: settingsData } = useSiteSettings(siteSlug);

  const createTheme = useCreateAccountTheme();
  const duplicateTheme = useDuplicateAccountTheme();
  const renameTheme = useRenameAccountTheme();
  const deleteTheme = useDeleteAccountTheme();
  const updateSettings = useUpdateSiteSettings(siteSlug);

  const [dirtyPath, setDirtyPath] = useState<string | null>(null);
  const [templatePath, setTemplatePath] = useState<string | null>(null);
  // Publish the open theme file so Baxter knows what "this file" means — it is
  // component state, not URL state, so the panel can't read it any other way.
  const setBaxterThemeFile = useEditorContext((s) => s.setThemeFile);
  useEffect(() => {
    setBaxterThemeFile(templatePath);
  }, [templatePath, setBaxterThemeFile]);
  useEffect(() => () => setBaxterThemeFile(null), [setBaxterThemeFile]);
  /** Bumped on every saved theme file — the preview rebuilds itself off this. */
  const [saveNonce, setSaveNonce] = useState(0);
  // AB#675: a Baxter theme write is a save we didn't make. The panel already
  // invalidated themeKeys/accountThemeKeys, so the tile list and file tree
  // refetch on their own — this is the one thing that only the parent can do:
  // bump the SAME nonce a manual save bumps, so the preview pane rebuilds
  // (PreviewPane folds it into `wantStamp`, which un-settles the auto-build).
  const themeChangedAt = useBaxterChanged('theme');
  useEffect(() => {
    if (themeChangedAt == null) return;
    setSaveNonce((n) => n + 1);
  }, [themeChangedAt]);
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameName, setRenameName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScope, setNewScope] = useState<AccountThemeScope>('account');
  /** Scope came from a column (or the Duplicate submenu) — no picker in the dialog. */
  const [scopeLocked, setScopeLocked] = useState(true);
  const [bundledId, setBundledId] = useState('');
  const [stockHidden, setStockHidden] = useState(storedStockHidden);

  const toggleStockHidden = () => {
    const next = !stockHidden;
    setStockHidden(next);
    try {
      localStorage.setItem(STOCK_HIDDEN_KEY, next ? '1' : '0');
    } catch {
      // storage disabled — the choice just doesn't survive a reload
    }
  };

  const theme = data?.data;
  const bundled = bundledData?.data ?? [];
  const appliedId = settingsData?.data?.account_theme_id ?? null;
  const accountThemes = accountThemesData?.data ?? [];
  const appliedTheme = accountThemes.find((t) => t.id === appliedId) ?? null;

  // --- blueprints ----------------------------------------------------------
  const blueprints: Blueprint[] = accountThemes.map(accountBlueprint);
  /**
   * AB#644 — the stock themes, shown in the Account column under this account's
   * own blueprints. They are the same list for everybody, ship in the image, and
   * have no row: nothing here can delete them. Grouped by what they are for, so
   * the two portfolio / consultant / blog pairs sit together.
   */
  const stockBlueprints: Blueprint[] = [...bundled]
    .sort(
      (a, b) =>
        (STOCK_ORDER[a.category ?? ''] ?? 99) - (STOCK_ORDER[b.category ?? ''] ?? 99) ||
        a.name.localeCompare(b.name),
    )
    .map(stockBlueprint);
  const all = [...blueprints, ...stockBlueprints];

  /**
   * The starred tile — what the site builds with. A blueprint wins when one is
   * applied; otherwise it is the bundled theme, i.e. a stock tile.
   */
  const appliedKey = appliedTheme
    ? blueprintKey(appliedTheme)
    : theme
      ? `stock:${theme.active_theme}`
      : null;

  const byColumn: Record<BlueprintColumn, Blueprint[]> = {
    account: [
      ...blueprints.filter((b) => b.column === 'account'),
      // Hiding the stock tiles must never hide the one the site BUILDS with —
      // the starred tile stays whatever the eye says.
      ...stockBlueprints.filter((b) => !stockHidden || b.key === appliedKey),
    ],
    site: blueprints.filter((b) => b.column === 'site'),
    user: blueprints.filter((b) => b.column === 'user'),
  };
  const selected =
    all.find((b) => b.key === bpParam) ??
    all.find((b) => b.key === appliedKey) ??
    all[0] ??
    null;

  const selectBlueprint = (b: Blueprint) =>
    navigate({ search: { bp: b.key }, replace: true });

  // --- actions -------------------------------------------------------------

  const handleApply = async (id: number | null) => {
    try {
      await updateSettings.mutateAsync({ account_theme_id: id });
      // The mutation only refreshes site settings, but starring/unstarring moves
      // `in_use_by` on BOTH blueprints — without this the tile that lost the star
      // keeps a stale "Delete — in use by 1 site" until a reload.
      await queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
      toast.success('Site theme updated');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  /**
   * New blueprints are always copied from a bundled theme; copying an existing
   * blueprint goes through Duplicate ▸ instead. `lockScope` is false only for the
   * page-level button, which is the one place the scope is still a question.
   */
  const openCreate = (scope: AccountThemeScope, name: string, lockScope = true) => {
    setNewScope(scope);
    setScopeLocked(lockScope);
    setNewName(name);
    setBundledId(theme?.active_theme ?? '');
    setCreateOpen(true);
  };

  const selectedBundled = bundledId || theme?.active_theme || bundled[0]?.id || '';
  /** Ordered like the Account column: general, then portfolio / consultant / blog. */
  const stockOptions = [...bundled].sort(
    (a, b) =>
      (STOCK_ORDER[a.category ?? ''] ?? 99) - (STOCK_ORDER[b.category ?? ''] ?? 99) ||
      a.name.localeCompare(b.name),
  );
  const bundledNameOf = (id: string) => bundled.find((b) => b.id === id)?.name ?? id;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    if (!selectedBundled) return;
    try {
      const created = await createTheme.mutateAsync({
        name,
        scope: newScope,
        site: siteSlug,
        from_bundled: selectedBundled,
      });
      setCreateOpen(false);
      setNewName('');
      setBundledId('');
      const id = created.data?.id;
      if (id) navigate({ search: { bp: `${newScope}:${id}` }, replace: true });
      toast.success('Blueprint created');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  /**
   * AB#644: build this site with a STOCK theme. That is `settings.theme`, not the
   * blueprint star — so any applied blueprint is cleared in the same request, or
   * it would keep winning at build time.
   */
  const handleUseStock = async (themeId: string) => {
    try {
      await updateSettings.mutateAsync({ theme: themeId, account_theme_id: null });
      await queryClient.invalidateQueries({ queryKey: themeKeys.all(siteSlug) });
      await queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
      toast.success('Site theme updated');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const handleDuplicate = async (b: Blueprint, target: DuplicateTarget) => {
    // A stock theme has no row to duplicate — copying one is an ordinary
    // "new blueprint from a bundled theme".
    if (b.stock) {
      try {
        const created = await createTheme.mutateAsync({
          name: b.name,
          scope: target,
          site: siteSlug,
          from_bundled: b.stock.id,
        });
        const id = created.data?.id;
        if (id) navigate({ search: { bp: `${target}:${id}` }, replace: true });
        toast.success('Blueprint created');
      } catch (err) {
        toast.error(errorText(err));
      }
      return;
    }
    try {
      const created = await duplicateTheme.mutateAsync({
        id: b.accountTheme!.id,
        name: `${b.name} copy`,
        scope: target,
        site: siteSlug,
      });
      const id = created.data?.id;
      if (id) navigate({ search: { bp: `${target}:${id}` }, replace: true });
      toast.success('Blueprint duplicated');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const handleDelete = async (b: Blueprint) => {
    if (!b.accountTheme) return; // stock tiles have no Delete item
    if (
      !(await confirmDialog(
        `Delete "${b.name}"? Any site using it reverts to its bundled theme. This cannot be undone.`,
        { title: 'Delete blueprint?', destructive: true, confirmLabel: 'Delete blueprint' },
      ))
    )
      return;
    try {
      // A 409 `in_use` lands here if the site list changed under us (the menu
      // item is disabled for the cases we know about) — its message is the toast.
      await deleteTheme.mutateAsync(b.accountTheme.id);
      if (selected?.key === b.key) navigate({ search: { bp: undefined }, replace: true });
      toast.success('Blueprint deleted');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const handleRename = async () => {
    if (renameId == null) return;
    const name = renameName.trim();
    if (!name) return;
    try {
      await renameTheme.mutateAsync({ id: renameId, name });
      setRenameId(null);
      toast.success('Blueprint renamed');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  // Editor state is remounted per blueprint (key=), so drop any stale dirty path.
  useEffect(() => {
    setDirtyPath(null);
    setTemplatePath(null);
  }, [selected?.key]);

  // --- render --------------------------------------------------------------

  if (isLoading || blueprintsLoading) {
    return (
      <div className="space-y-4">
        <h1 className="mb-6 text-2xl font-semibold">Theme</h1>
        <Skeleton className="h-44 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }
  if (error || !theme) {
    return <div className="py-12 text-center text-muted-foreground">Error loading theme</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Theme</h1>
        <p className="text-sm text-muted-foreground">
          Blueprints are reusable copies of a theme. Pick one to edit; the starred one is what
          this site builds with.
        </p>
      </div>

      <BlueprintColumns
        byColumn={byColumn}
        selectedKey={selected?.key ?? null}
        appliedKey={appliedKey}
        onSelect={selectBlueprint}
        actions={{
          onDuplicate: handleDuplicate,
          onRename: (b) => {
            if (!b.accountTheme) return; // stock tiles have no Rename item
            setRenameId(b.accountTheme.id);
            setRenameName(b.name);
          },
          // A stock tile isn't a blueprint, so "use it" means switching the
          // site's BUNDLED theme (and clearing any starred blueprint first).
          onSetDefault: (b, next) => {
            if (b.stock) return handleUseStock(b.stock.id);
            handleApply(next && b.accountTheme ? b.accountTheme.id : null);
          },
          onDelete: handleDelete,
        }}
        stockHidden={stockHidden}
        onToggleStock={toggleStockHidden}
        onNewBlueprint={(scope) =>
          openCreate(scope, bundledNameOf(theme.active_theme))
        }
        newBlueprintButton={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openCreate('account', bundledNameOf(theme.active_theme), false)}
          >
            New Theme
          </Button>
        }
        siteAction={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openCreate('site', bundledNameOf(theme.active_theme))}
          >
            New Theme
          </Button>
        }
      />

      <div className="grid items-start gap-6 xl:grid-cols-2">
        {selected ? (
          <div className="space-y-4">
            {/* A stock theme's blurb and actions sit ABOVE its (read-only) files —
                never in place of the preview, which is what people came for. */}
            {selected.stock && (
              <div className="rounded-lg border p-4">
                <p className="font-medium">{selected.name}</p>
                {selected.description && (
                  <p className="mt-1 text-sm text-muted-foreground">{selected.description}</p>
                )}
                <p className="mt-2 text-sm text-muted-foreground">
                  A theme that ships with WordPuppi — available to every site on this account, and
                  it can&apos;t be edited or deleted. Duplicate it to get a blueprint you can
                  change.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => handleDuplicate(selected, 'account')}>
                    Duplicate to Account
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleUseStock(selected.stock!.id)}
                    disabled={selected.key === appliedKey || updateSettings.isPending}
                  >
                    {selected.key === appliedKey ? 'Site theme ✓' : 'Use as site theme'}
                  </Button>
                </div>
              </div>
            )}
            <BlueprintTabs
              key={selected.key}
              blueprint={selected}
              siteSlug={siteSlug}
              onDirtyPathChange={setDirtyPath}
              onSelectedPathChange={setTemplatePath}
              onSaved={() => setSaveNonce((n) => n + 1)}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed px-6 py-16 text-center">
            <p className="text-lg font-medium">No blueprint yet</p>
            <p className="mx-auto mt-1 max-w-sm text-muted-foreground">
              Create a blueprint above to start editing this site&apos;s look.
            </p>
          </div>
        )}

        <PreviewPane
          siteSlug={siteSlug}
          dirtyPath={dirtyPath}
          templatePath={templatePath}
          previewThemeId={selected?.accountTheme?.id}
          // AB#644: a stock theme has no blueprint id — preview it by name, so
          // every tile is previewable and the pane never disappears.
          previewTheme={selected?.stock?.id}
          rebuildNonce={saveNonce}
          note={
            selected && selected.key !== appliedKey
              ? `Previewing "${selected.name}" — the site still builds with the ⭐ default.`
              : undefined
          }
        />
      </div>

      {/* Create blueprint */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {scopeLocked ? `New ${SCOPE_TITLE[newScope]} Theme` : 'New Theme'}
            </DialogTitle>
            <DialogDescription>
              A copy of a WordPuppi theme you can edit. It doesn&apos;t change when the theme it
              was copied from does.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!scopeLocked && (
              <div className="space-y-2">
                <Label htmlFor="blueprintScope">Belongs to</Label>
                <Select value={newScope} onValueChange={(v) => setNewScope(v as AccountThemeScope)}>
                  <SelectTrigger id="blueprintScope" className="w-full">
                    <SelectValue>
                      {(v: unknown) => SCOPE_OPTION[v as AccountThemeScope] ?? String(v)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account">{SCOPE_OPTION.account}</SelectItem>
                    <SelectItem value="site">{SCOPE_OPTION.site}</SelectItem>
                    <SelectItem value="user">{SCOPE_OPTION.user}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="blueprintBundled">Stock theme</Label>
              <Select
                value={selectedBundled}
                onValueChange={(id) => {
                  setBundledId(id);
                  // Keep the name in step with the source until the user edits it.
                  if (!newName.trim() || newName === bundledNameOf(selectedBundled)) {
                    setNewName(bundledNameOf(id));
                  }
                }}
                disabled={bundled.length === 0}
              >
                <SelectTrigger id="blueprintBundled" className="w-full">
                  <SelectValue placeholder="Select a theme">
                    {(v: unknown) => bundledNameOf(String(v))}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {stockOptions.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* The stock themes live here too, so hiding them from the Account
                  column never puts them out of reach. */}
              <p className="text-sm text-muted-foreground">
                {bundled.find((b) => b.id === selectedBundled)?.description ??
                  'A copy you can edit. It stays as it is when the theme it came from changes.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="blueprintName">Name</Label>
              <Input
                id="blueprintName"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="My Blue Theme"
                autoFocus
              />
            </div>
            {newScope === 'user' && (
              <p className="text-sm text-muted-foreground">
                User blueprints are yours on every account; duplicate to Account to use one as a
                site default.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={
                !newName.trim() ||
                !selectedBundled ||
                createTheme.isPending
              }
            >
              {createTheme.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename blueprint */}
      <Dialog open={renameId != null} onOpenChange={(open) => !open && setRenameId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename blueprint</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="renameThemeName">Name</Label>
            <Input
              id="renameThemeName"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRenameId(null)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={!renameName.trim() || renameTheme.isPending}>
              {renameTheme.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
